import {
  type AddressingOptions,
  type SkillRequired,
} from '@funnellab/shared/src/types/directus-enums';
import type { CollectionType, QueryItem } from '@directus/sdk';
import type { CustomDirectusTypes } from '@funnellab/shared/src/types/directus.ts';
import type { EmployerBrandDTO } from '@funnellab/shared/src/dtos/EmployerBrandDTO';
import type { EmployerBrandDto } from './employerBrand';
import type { JobAttributeDTO } from '@funnellab/shared/src/dtos/JobAttributeDTO';
import type {
  JobAttributeDTOForUpdate,
  JobDTO,
  JobDTOAttributesResolved,
  JobDTORelationsResolved,
  JobLocationForUpdate,
  JobRelationsKeys,
} from '@funnellab/shared/src/dtos/JobDTO';
import type { LocationDTO } from '@funnellab/shared/src/dtos/LocationDTO';
import type { NoteDTO } from '@funnellab/shared/src/dtos/NoteDTO';
import type { OrganizationDto } from './organization';
import type { UserDTO } from '@funnellab/shared/src/dtos/UserDTO';

export type JobHookParameters = {
  targetCareerJumpers?: boolean | null;
  addressing?: `${AddressingOptions}` | null;
  targetJobTitle?: string[] | undefined;
  targetExperience?: string[] | undefined;
  targetEducation?: string[] | undefined;
  targetSkill?: string[] | undefined;
  locations?: string[] | undefined;
};

export const detailRelations: JobRelationsKeys[] = [
  'organization',
  'employerBrand',
  'contacts',
  'notes',
  'locations',
];

export const wizardRelations: JobRelationsKeys[] = [
  'organization',
  'employerBrand',
  'contacts',
  'files',
  'locations',
  'benefits',
  'educations',
  'experiences',
  'skills',
  'softSkills',
  'tasks',
  'targetEducations',
  'targetExperiences',
  'targetJobTitles',
  'targetSkills',
];

export type JobDetailsDTO = Pick<
  JobDTO,
  | 'id'
  | 'title'
  | 'status'
  | 'nextRenewal'
  | 'idExternal'
  | 'invoiceReference'
  | 'monthlyCampaignBudget'
  | 'applicantManagementUrl'
  | 'adText'
  | 'addressing'
  | 'useFunnellabChatbot'
  | 'useFunnellabATS'
  | 'scenes'
> &
  Required<
    Pick<
      JobDTORelationsResolved,
      'organization' | 'employerBrand' | 'contacts' | 'notes' | 'locations'
    >
  >;

export type JobDetails = Pick<
  JobDetailsDTO,
  | 'id'
  | 'title'
  | 'status'
  | 'nextRenewal'
  | 'idExternal'
  | 'invoiceReference'
  | 'monthlyCampaignBudget'
  | 'applicantManagementUrl'
  | 'adText'
  | 'addressing'
  | 'useFunnellabChatbot'
  | 'useFunnellabATS'
  | 'scenes'
> &
  Required<Pick<JobDTORelationsResolved, 'organization' | 'employerBrand'>> & {
    contacts: (Partial<Omit<UserDTO, 'id'>> & {
      id?: number;
      directus_users_id: string;
    })[];
    notes: (NoteDTO | { content: string })[];
    locations: (JobLocationForUpdate | LocationDTO)[];
  };

export type JobDetailsForUpdate = Partial<JobDetails>;

export type JobWizardDTO = JobDTO &
  JobDTORelationsResolved &
  JobDTOAttributesResolved;

export type JobForWizard = Partial<
  Pick<
    JobWizardDTO,
    | 'files'
    | 'acceptedTermsAndConditions'
    | 'addressing'
    | 'boost'
    | 'id'
    | 'invoiceReference'
    | 'isFullTime'
    | 'isPartTime'
    | 'linksToExternalFiles'
    | 'monthlyCampaignBudget'
    | 'numberOfPlannedHires'
    | 'runtimeMonths'
    | 'salaryMax'
    | 'salaryMin'
    | 'salaryType'
    | 'showSalary'
    | 'status'
    | 'stripeCheckoutId'
    | 'targetCareerJumpers'
    | 'title'
    | 'type'
    | 'workExperience'
    | 'hook'
  > & {
    organization: JobDTORelationsResolved['organization'] | OrganizationDto;
    educations: JobAttributeType[];
    targetExperiences: JobAttributeType[];
    targetEducations: JobAttributeType[];
    targetSkills: JobAttributeType[];
    experiences: JobAttributeType[];
    targetJobTitles: JobAttributeType[];
    benefits: JobAttributeType[];
    skills: JobWizardSkill[];
    softSkills: JobAttributeType[];
    tasks: (JobAttributeDTOForUpdate | JobAttributeDTO)[];
    contacts: (Partial<Omit<UserDTO, 'id'>> & {
      id?: number;
      directus_users_id: string;
    })[];
    locations: (JobLocationForUpdate | LocationDTO)[];
    employerBrand: EmployerBrandDTO | EmployerBrandDto | null;
    userCampaignStartDate: Date | null;
  }
>;

export type JobAttributeType = JobAttributeDTO | JobAttributeDTOForUpdate;
export type JobWizardSkill = JobAttributeType & {
  required: `${SkillRequired}`;
};

export const jobTableQuery = {
  fields: [
    'id',
    'idExternal',
    'title',
    'status',
    'nextRenewal',
    'date_updated',
    { organization: ['name'] },
  ],
} as const satisfies QueryItem<
  CustomDirectusTypes,
  CollectionType<CustomDirectusTypes, 'job'>
>;
